import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal';
import MitteilungAnhaenge from './MitteilungAnhaenge';
import MitteilungTerminInfo from './MitteilungTerminInfo';
import MitteilungAntwortAktionen from './MitteilungAntwortAktionen';
import { Mitteilungen } from '../api/apiClient';
import { formatDatumZeit } from '../utils/format';

/**
 * Zeigt beim Aufruf der Anwendung jeden noch nicht beantworteten Eintrag als
 * Modal -- einen nach dem anderen, aeltester zuerst.
 *
 * Zwei Wege heraus:
 * - "Später erinnern" schliesst nur diese Ansicht. Bewusst nichts gespeichert:
 *   beim naechsten Aufruf der Seite erscheint der Eintrag erneut.
 * - Die Antwort (Kenntnisnahme bzw. Zu-/Absage) wird serverseitig
 *   festgehalten; danach poppt der Eintrag nicht mehr auf, bleibt aber unter
 *   Allgemein → Mitteilungen lesbar und dort auch aenderbar.
 */
function MitteilungenPopup() {
  const [offene, setOffene] = useState([]);
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    let abgebrochen = false;
    Mitteilungen.getOffen()
      .then((daten) => {
        if (!abgebrochen) setOffene(Array.isArray(daten) ? daten : []);
      })
      // Eine fehlgeschlagene Abfrage darf die Seite nicht blockieren.
      .catch(() => {
        if (!abgebrochen) setOffene([]);
      });
    return () => {
      abgebrochen = true;
    };
  }, []);

  const aktuelle = offene[index] ?? null;
  if (!aktuelle) return null;

  const weiter = () => {
    setFehler(null);
    setIndex((i) => i + 1);
  };

  const handleAntwort = async (antwort) => {
    setSaving(true);
    setFehler(null);
    try {
      await Mitteilungen.antworten(aktuelle.mitteilungsId, antwort);
      setIndex((i) => i + 1);
    } catch (e) {
      setFehler(e.message);
    } finally {
      setSaving(false);
    }
  };

  const istTermin = aktuelle.art === 'termin';
  const verbleibend = offene.length - index - 1;

  return (
    <Modal show onHide={weiter} size="lg">
      <Modal.Header onClose={weiter}>
        {istTermin ? 'Termin' : 'Mitteilung'}
        {verbleibend > 0 && (
          <span className="badge badge-gray ml-2 align-middle">{verbleibend + 1} offen</span>
        )}
      </Modal.Header>
      <Modal.Body>
        <h3 className="text-lg font-semibold text-slate-900">{aktuelle.titel}</h3>

        {aktuelle.beschreibung && (
          <p className="whitespace-pre-wrap text-sm text-slate-700">{aktuelle.beschreibung}</p>
        )}

        <MitteilungTerminInfo mitteilung={aktuelle} onFehler={setFehler} />

        <MitteilungAnhaenge anhaenge={aktuelle.anhaenge} onFehler={setFehler} onNavigate={weiter} />

        {aktuelle.zusageErforderlich && (
          <p className="alert-info">Für diesen Termin wird eine Zu- oder Absage erwartet.</p>
        )}

        <p className="text-xs text-slate-400">
          Eingestellt am {formatDatumZeit(aktuelle.erstelltAm)}
          {aktuelle.erstellerName ? ` von ${aktuelle.erstellerName}` : ''}
        </p>

        {fehler && <p className="alert-error">{fehler}</p>}
      </Modal.Body>
      <Modal.Footer>
        <Link to="/allgemein/mitteilungen" onClick={weiter} className="mr-auto text-xs text-slate-500 hover:text-slate-700">
          Alle Mitteilungen
        </Link>
        <button className="btn btn-secondary" onClick={weiter} disabled={saving}>
          Später erinnern
        </button>
        <MitteilungAntwortAktionen
          mitteilung={aktuelle}
          onAntwort={handleAntwort}
          laufend={saving}
        />
      </Modal.Footer>
    </Modal>
  );
}

export default MitteilungenPopup;
